import { StudentType } from '@wonderschool/common-base-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { Timestamp } from 'firebase/firestore';
import { SERVICE_DIRECTION } from '../contacts/enums';
import { normalizeBillingInterval } from '../helpers/invoicePlans';
import { convertToDollars } from '../helpers/money';
import { capitalizeFirstLetter } from '../helpers/utils';
import { BillingIntervalEnum } from './enums';
import { FeeType, TuitionType } from './types';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const formatDate = (date: Timestamp | Date | undefined) => {
  if (!date) return null;
  let currentDate;
  if (date instanceof Timestamp) {
    currentDate = dayjs.unix(date.seconds).utc().format('YYYY-MM-DD');
  } else {
    currentDate = dayjs(date).utc().format('YYYY-MM-DD');
  }
  const d = currentDate.split('-');
  const convertedLocal = dayjs(`${d[0]}-${d[1]}-${d[2]}`, 'YYYY-MM-DD');
  return convertedLocal.valueOf();
};

const formatInvoiceItemList = (fee: FeeType) => {
  return {
    category: fee.name,
    item: fee.name,
    notes: '',
    amount: convertToDollars(fee.amount || 0),
    discounts: [],
  };
};

const formatInvoiceItems = (fee: FeeType) => {
  return {
    amount: fee.amount,
    currency: 'usd',
    description: fee.name,
  };
};

const formatPlanItemsList = (tuition: TuitionType, student: StudentType) => {
  return {
    amount: convertToDollars(tuition.amount || 0),
    item: `Tuition - ${student?.enrollment?.scheduledDays?.length} days`,
    notes: '',
    discounts: [],
    category: 'Tuition',
  };
};

export const convertDataToInvoice = (fee, loggedInUser, student) => {
  return {
    details: {
      dateDue: formatDate(fee.dueDate),
      dateServiceStart: null,
      dateServiceEnd: null,
      invoiceItemList: [formatInvoiceItemList(fee)],
      total: convertToDollars(fee.amount),
      isInvoice: true,
      alreadyPaid: false,
      manuallyPaid: false,
      paidAmount: 0,
      status: 'draft',
      createdBy:
        loggedInUser.uid && loggedInUser.displayName
          ? { id: loggedInUser.uid, displayName: loggedInUser.displayName }
          : {},
    },
    items: [formatInvoiceItems(fee)],
    students: [student],
  };
};

export const convertDataToPlan = (tuition, loggedInUser, student, planEndDate) => {
  const notificationDate = dayjs.unix(tuition.dueDate.seconds).utc().subtract(3, 'days').toDate();
  const dateStart = dayjs.unix(tuition.firstServiceStart.seconds).utc().startOf('month').toDate();
  const dateDue = dayjs.unix(tuition.dueDate.seconds).utc().toDate();
  return {
    isInvoice: false,
    createdBy:
      loggedInUser.uid && loggedInUser.displayName
        ? { id: loggedInUser.uid, displayName: loggedInUser.displayName }
        : {},
    notificationDays: 3,
    billingInterval: normalizeBillingInterval(capitalizeFirstLetter(tuition.billingInterval)),
    dateStart: formatDate(dateStart),
    dateEnd: planEndDate ? formatDate(planEndDate) : null,
    dateDue: formatDate(dateDue),
    dateSend: formatDate(notificationDate),
    status: 'active',
    total: convertToDollars(tuition.amount),
    invoiceItemList: [formatPlanItemsList(tuition, student)],
    students: [student],
    serviceDirection: tuition.billFor === 'Prior' ? SERVICE_DIRECTION.Previous : SERVICE_DIRECTION.Next,
    createdFromEnrollment: true,
  };
};

export const convertDataToProrateInvoice = (
  {
    proratedAmount: amount = 0,
    proratedDueDate: dueDate,
    billingInterval,
    billFor,
    proratedPeriod,
    isProrated,
  }: TuitionType,
  loggedInUser,
  student
) => {
  if (!dueDate) return {};
  const item = isProrated ? `Prorated tuition` : `Tuition - ${student?.enrollment?.scheduledDays?.length} days`;

  const dateServiceEnd =
    billingInterval === BillingIntervalEnum.MONTHLY
      ? dayjs.unix(dueDate.seconds).endOf('month').utc().toDate()
      : dayjs.unix(dueDate.seconds).endOf('week').utc().toDate();

  return {
    details: {
      dateDue: formatDate(dueDate),
      dateServiceStart: isProrated
        ? formatDate(dueDate)
        : formatDate(dayjs.unix(dueDate.seconds).startOf('month').utc().toDate()),
      dateServiceEnd: formatDate(dateServiceEnd),
      invoiceItemList: [
        {
          amount: amount,
          item,
          notes: '',
          discounts: [],
          category: 'Tuition',
        },
      ],
      total: amount,
      isInvoice: true,
      alreadyPaid: false,
      manuallyPaid: false,
      paidAmount: 0,
      status: 'draft',
      serviceDirection: billFor === 'Prior' ? SERVICE_DIRECTION.Previous : SERVICE_DIRECTION.Next,
      createdBy:
        loggedInUser.uid && loggedInUser.displayName
          ? { id: loggedInUser.uid, displayName: loggedInUser.displayName }
          : {},
      servicePeriod: proratedPeriod,
    },
    items: [
      {
        amount: amount,
        currency: 'usd',
        description: 'Prorate',
      },
    ],
    students: [student],
  };
};
